// GroupSelector — pick Stride groups (search, chips, remove).
//
// The results list is PORTALLED to <body> at a fixed position under the
// search box (2026-10-07): it used to be absolutely positioned inside the
// picker, so any card, panel or modal with overflow hidden — the Settings
// cards are — cut it off, and the groups could not be seen.
// emptyHint: the line under an empty selection (null = none) — what "no
// groups" MEANS differs per caller (visible to all vs. admins only).

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '@app/services/api';
import '@platform/ui/GroupSelector/GroupSelector.css';

export default function GroupSelector({ groups = [], onChange, label, compact = false, emptyHint = 'No groups selected — visible to all users.', placeholder = 'Search groups…' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const open = results.length > 0;

  // Close the list on an outside click — the list itself lives in <body>.
  useEffect(() => {
    const handleClick = (e) => {
      const inside = (wrapperRef.current && wrapperRef.current.contains(e.target))
        || (listRef.current && listRef.current.contains(e.target));
      if (!inside) {
        setResults([]);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keep the floating list under the search box while open.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setAnchor({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open]);

  const handleSearch = (value) => {
    setQuery(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!value.trim()) { setResults([]); return; }
    timeoutRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const data = await api.getGroups(value);
        setResults((data.groups || data || []).filter(g => !groups.includes(g.name)));
      } catch (err) {
        console.error('Group search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const addGroup = (name) => {
    if (!groups.includes(name)) {
      onChange([...groups, name]);
    }
    setQuery('');
    setResults([]);
  };

  const removeGroup = (name) => {
    onChange(groups.filter(g => g !== name));
  };

  return (
    <div className={`group-selector ${compact ? 'group-selector--compact' : ''}`} ref={wrapperRef}>
      {label && <label className="group-selector-label">{label}</label>}

      {groups.length > 0 && (
        <div className="group-selector-tags">
          {groups.map(name => (
            <span key={name} className="group-selector-tag">
              {name}
              <button className="group-selector-tag-remove" onClick={() => removeGroup(name)} title="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="group-selector-search-wrap">
        <input
          ref={inputRef}
          type="text"
          className="group-selector-search"
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
        />
        {searching && <span className="group-selector-searching">Searching...</span>}
        {open && anchor && createPortal(
          <div
            ref={listRef}
            className="group-selector-dropdown group-selector-dropdown--floating"
            style={{ top: anchor.top, left: anchor.left, width: anchor.width }}
          >
            {results.map(g => (
              <button key={g.name} type="button" className="group-selector-dropdown-item" onClick={() => addGroup(g.name)}>
                {g.name}
              </button>
            ))}
          </div>,
          document.body,
        )}
      </div>

      {groups.length === 0 && !compact && emptyHint && (
        <div className="group-selector-hint">{emptyHint}</div>
      )}
    </div>
  );
}
